import { MenuItem, menuItemClasses, MenuList, Popover } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import {
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridRowsProp,
} from '@mui/x-data-grid'
import AvatarComponent from 'components/avatar'
import ButtonMobileComponent from 'components/button-mobile'
import {
  ArrivedLabel,
  CheckIcon,
  DeleteIcon,
  DollarIcon,
  EditUserIcon,
  MegaphoneIcon,
  MoreVerticalDotOnlyIcon,
  OnMyWayLabel,
  PlusIcon,
  SendIcon,
  SmsIcon,
  UploadIcon,
} from 'components/icons'
import ModalConfirmComponent from 'components/modal-confirm'
import RadioMobileComponent from 'components/radio-mobile'
import TableComponent, { WrappedHeaderText } from 'components/table'
import CircularProgressWithLabel from 'components/table/CircularProgressLabel'
import DeleteStudentComponent from 'components/table/DeleteStudentComponent'
import FileUpload from 'components/table/FileUpload'
import NotifyUserComponent from 'components/table/NotifyUserComponent'
import RegisterUserComponent from 'components/table/RegisterUserComponent'
import UpsertStudentComponent from 'components/table/UpsertStudentComponent'
import TextComponent from 'components/text'
import ToastComponent from 'components/toast'
import hide from 'images/hide.png'
import show from 'images/show.png'
import { parsePhoneNumber } from 'libphonenumber-js/max'
import Papa from 'papaparse'
import { useEffect, useState } from 'react'
import { CommunityType } from 'types/CommunityTypes'
import { CreateNotificationInputProps, CreateNotificationProps, NotifyTypes } from 'types/NotificationTypes'
import { SchoolUsersStudentProps } from 'types/SchoolTypes'
import { UpsertStudentProps } from 'types/StudentTypes'
import {
  defaultPhoneNumberCountry,
  isPaidTransportFeatureEnabled,
  titleCase,
  titleCaseSchoolNameForUI,
} from 'utils/constants'
import { createNotification } from 'utils/notifications'
import { deleteStudents, upsertStudents } from 'utils/students'
import { MessageProps, ScheduleGroupProps, SchoolUsersProps, VehicleProps } from 'utils/types'
import { isValidName, isValidPhone, isValidRelationship, isValidScheduleGroup } from 'utils/validations'
import './index.css'
import useDeviceSize, { DeviceSize } from 'hooks/useDeviceSize'
import { updateSchool } from 'utils/schools'

// --- Constants ---
const SHOW_MASKED_PHONENUMBER_ON_UI: boolean = true
const defaultColumnWidth: number = 120

const startDismissalButtonConfig: Record<DeviceSize, { text: string; width: string; padx: string }> = {
  [DeviceSize.xs]: { text: 'Start Dismissal', width: 'w-[120px]', padx: 'px-[8px]' },
  [DeviceSize.sm]: { text: 'Start Dismissal', width: 'w-[120px]', padx: 'px-[8px]' },
  [DeviceSize.md]: { text: 'Start Dismissal', width: 'w-[120px]', padx: 'px-[8px]' },
  [DeviceSize.lg]: { text: 'Start Dismissal', width: 'w-[120px]', padx: 'px-[8px]' },
  [DeviceSize.xl]: { text: 'Start Dismissal', width: 'w-[120px]', padx: 'px-[8px]' },
}

const stopDismissalButtonConfig: Record<DeviceSize, { text: string; width: string; padx: string }> = {
  [DeviceSize.xs]: { text: 'Stop Dismissal', width: 'w-[120px]', padx: 'px-[8px]' },
  [DeviceSize.sm]: { text: 'Stop Dismissal', width: 'w-[120px]', padx: 'px-[8px]' },
  [DeviceSize.md]: { text: 'Stop Dismissal', width: 'w-[120px]', padx: 'px-[8px]' },
  [DeviceSize.lg]: { text: 'Stop Dismissal', width: 'w-[120px]', padx: 'px-[8px]' },
  [DeviceSize.xl]: { text: 'Stop Dismissal', width: 'w-[120px]', padx: 'px-[8px]' },
}

const messageButtonConfig: Record<DeviceSize, { text: string; width: string; padx: string }> = {
  [DeviceSize.xs]: { text: 'Announce', width: 'w-[110px]', padx: 'px-[8px]' },
  [DeviceSize.sm]: { text: 'Announce', width: 'w-[110px]', padx: 'px-[8px]' },
  [DeviceSize.md]: { text: 'Announce', width: 'w-[110px]', padx: 'px-[8px]' },
  [DeviceSize.lg]: { text: 'Announce', width: 'w-[110px]', padx: 'px-[8px]' },
  [DeviceSize.xl]: { text: 'Announce', width: 'w-[110px]', padx: 'px-[8px]' },
}

const uploadButtonConfig: Record<DeviceSize, { text: string; width: string; padx: string }> = {
  [DeviceSize.xs]: { text: 'Upload Families', width: 'w-[150px]', padx: 'px-[8px]' },
  [DeviceSize.sm]: { text: 'Upload Families', width: 'w-[150px]', padx: 'px-[8px]' },
  [DeviceSize.md]: { text: 'Upload Families', width: 'w-[150px]', padx: 'px-[8px]' },
  [DeviceSize.lg]: { text: 'Upload Families', width: 'w-[150px]', padx: 'px-[8px]' },
  [DeviceSize.xl]: { text: 'Upload Families', width: 'w-[150px]', padx: 'px-[8px]' },
}

const addButtonConfig: Record<DeviceSize, { text: string; width: string; padx: string }> = {
  [DeviceSize.xs]: { text: 'Add Family', width: 'w-[120px]', padx: 'px-[8px]' },
  [DeviceSize.sm]: { text: 'Add Family', width: 'w-[120px]', padx: 'px-[8px]' },
  [DeviceSize.md]: { text: 'Add Family', width: 'w-[120px]', padx: 'px-[8px]' },
  [DeviceSize.lg]: { text: 'Add Family', width: 'w-[120px]', padx: 'px-[8px]' },
  [DeviceSize.xl]: { text: 'Add Family', width: 'w-[120px]', padx: 'px-[8px]' },
}

// --- Helper Functions ---

const createUploadErrorMessage = (erroredRows: { row: number; error: string }[]): MessageProps => {
  // Log detailed errors to console
  console.error('Bulk upload validation errors:')
  erroredRows.forEach((row) => {
    console.error(`Row ${row.row}: ${row.error}`)
  })

  let text: string
  if (erroredRows.length > 7)
    text = `Invalid entries in ${erroredRows.length} rows (rows ${erroredRows
      .slice(0, 7)
      .map((row) => row.row)
      .join(', ')} and more). Check these rows and try again. Contact support@carpool.school for help.`
  else if (erroredRows.length > 1)
    text = `Invalid entries in rows: ${erroredRows.map((row) => row.row).join(', ')}. Check these rows and try again. Contact support@carpool.school for help.`
  else
    text = `Invalid entry in row ${erroredRows[0].row}. Check this row and try again. Contact support@carpool.school for help.`
  return {
    style: 'error',
    heading: 'CSV Upload Error',
    text,
  }
}

function Index({
  schoolUsers,
  failedAvatarUrls,
  onFailedAvatarUrl,
  onUpdate,
}: {
  schoolUsers?: SchoolUsersProps | null
  failedAvatarUrls: Set<string>
  onFailedAvatarUrl: (url: string) => void
  onUpdate: (cb?: () => void) => void
}): JSX.Element {
  const deviceSize: DeviceSize = useDeviceSize()
  // All states at the top
  const [viewMode, setViewMode] = useState<'ALL' | 'CARPOOL' | 'DISMISSAL'>('ALL')
  const [isDismissalModalOpen, setIsDismissalModalOpen] = useState(false)
  const [isDismissalActive, setIsDismissalActive] = useState(schoolUsers?.schoolDismissalSession ? true : false)
  const [dismissalModeDate, setDismissalModeDate] = useState<string>(
    schoolUsers?.schoolDismissalSession || new Date().toISOString().split('T')[0],
  )
  const [revealedPhoneRows, setRevealedPhoneRows] = useState<Set<string>>(new Set())
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([])
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isRegisterDialogOpen, setIsRegisterDialogOpen] = useState<boolean>(false)
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState<boolean>(false)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState<boolean>(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false)
  const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false)
  const [isRemindDialogOpen, setIsRemindDialogOpen] = useState<boolean>(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<string>('')
  const [msg, setMsg] = useState<MessageProps>()

  useEffect(() => {
    if (isDismissalActive) {
      setViewMode('DISMISSAL')
      setDismissalModeDate(new Date().toISOString().split('T')[0])
    } else {
      setViewMode('ALL')
    }
  }, [isDismissalActive])

  if (!schoolUsers) return <LinearProgress />

  // Now destructure, etc.
  const {
    schoolName,
    isCarpoolManagementActive,
    isDismissalManagementActive,
    isCommunityAnnouncementsActive,
    schoolScheduleGroups,
    students,
  } = schoolUsers

  // --- Event Handlers ---

  const handlePhoneRevealToggle = (id: string) => {
    setRevealedPhoneRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id) // If the ID is already in the Set, remove it (hide)
      } else {
        next.add(id) // If the ID is not in the Set, add it (reveal)
      }
      return next
    })
  }

  const handleRowSelectionChange = (rowSelectionModel: GridRowSelectionModel) => {
    setSelectedIds(rowSelectionModel)
  }

  const processBulkUploadData = (data: any[], schoolScheduleGroups: ScheduleGroupProps[] | undefined) => {
    const validStudents: UpsertStudentProps[] = []
    const invalidRows: { row: number; error: string }[] = []

    for (let i = 0; i < data.length; i++) {
      const { Id, Phone, Name, Relation = 'Other', Schedule, Student } = data[i]

      // Skip empty rows (where all fields are empty or whitespace)
      const isEmptyRow = [Id, Phone, Name, Schedule, Student].every((field) => !field?.toString().trim())
      if (isEmptyRow) continue

      const isNewUser: boolean = !Id
      const scheduleGroupName: string =
        schoolScheduleGroups?.length === 1 ? schoolScheduleGroups[0].schoolScheduleGroupName : Schedule

      // Skip rows without phone number
      if (!Phone) {
        invalidRows.push({ row: i + 2, error: 'Phone number is required' })
        continue
      }

      // Validate existing users
      const existingUserData = !isNewUser ? students?.find((s) => s.studentId === Id) : null
      if (!isNewUser) {
        if (!existingUserData) {
          invalidRows.push({ row: i + 2, error: 'This ID does not exist in the system' })
          continue
        } else if (Name !== existingUserData.userName || Relation !== existingUserData.userRelationship) {
          // Only prevent changes if user is verified
          if (existingUserData.isVerified) {
            invalidRows.push({
              row: i + 2,
              error: 'Admins cannot change name or relationship after user is registered',
            })
            continue
          }
        }
      }

      // Validate phone number
      const isPhoneValid = isValidPhone(Phone, true)
      if (!isPhoneValid) {
        invalidRows.push({ row: i + 2, error: 'Invalid phone number format' })
        continue
      }

      // Validate name
      const isNameValid = isValidName(Name, true)
      if (!isNameValid) {
        invalidRows.push({ row: i + 2, error: 'Invalid name format' })
        continue
      }

      // Validate relationship
      const isRelationshipValid = isValidRelationship(Relation, isNewUser)
      if (!isRelationshipValid) {
        invalidRows.push({ row: i + 2, error: 'Invalid relationship type' })
        continue
      }

      // Validate schedule group
      const isScheduleValid = isValidScheduleGroup(
        schoolScheduleGroups?.map((v) => v.schoolScheduleGroupName) ?? [],
        scheduleGroupName,
        isNewUser,
      )
      if (!isScheduleValid) {
        invalidRows.push({ row: i + 2, error: 'Invalid schedule group' })
        continue
      }

      // If all validations pass, add to valid students
      validStudents.push({
        userPhoneNumber: parsePhoneNumber(Phone, defaultPhoneNumberCountry).format('E.164'),
        userName: Name,
        userRelationship: Relation,
        studentId: Id,
        studentName: Student,
        scheduleGroupName,
      })
    }

    return { validStudents, invalidRows }
  }

  const handleBulkUpload = async (fileData: any) => {
    setIsLoading('Uploading Users...')
    const { validStudents, invalidRows } = processBulkUploadData(fileData, schoolScheduleGroups)

    if (invalidRows.length > 0) {
      setMsg(createUploadErrorMessage(invalidRows))
      setIsLoading('')
      return
    }

    if (!validStudents?.length) {
      setMsg({ style: 'error', text: 'Your CSV file appears empty or invalid.' })
      setIsLoading('')
      return
    }

    if (validStudents.length > 100) {
      setMsg({ style: 'error', text: 'CSV file exceeds the 100 row limit.' })
      setIsLoading('')
      return
    }

    const { msg, data, error } = await upsertStudents(validStudents)
    setMsg(msg)
    if (error) {
      setMsg(msg)
      setIsLoading('')
    } else {
      onUpdate(() => {
        setMsg({
          style: data?.errored?.length ? 'error' : 'success',
          text: `${data?.created?.length ?? 0} added, ${data?.updated?.length ?? 0} updated, ${data?.errored?.length ?? 0} errored.`,
        })
        setIsLoading('')
      })
    }
  }

  /* --- Popover Menu and Modal Handlers --- */
  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, row: any) => {
    setAnchorEl(event.currentTarget) // sets anchor for popover
    setSelectedUser(row) // sets selected user
  }

  const handleCloseMenu = () => {
    setAnchorEl(null) // closes popover
    setSelectedUser(null) // clears selected user
  }

  const handleOpenRegisterDialog = () => {
    setIsRegisterDialogOpen(true) // opens register dialog
    setAnchorEl(null) // closes popover
  }

  const handleCloseRegisterDialog = () => {
    setIsRegisterDialogOpen(false) // closes register dialog
    setSelectedUser(null) // clears selected user
  }

  const handleOpenEditDialog = () => {
    setIsEditDialogOpen(true) // opens upsert dialog
    setAnchorEl(null) // closes popover
  }

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false) // closes upsert dialog
    setSelectedUser(null) // clears selected user
  }

  const handleOpenRemindDialog = () => {
    setIsRemindDialogOpen(true) // opens notify dialog
    setAnchorEl(null) // closes popover
  }

  const handleCloseRemindDialog = () => {
    setIsRemindDialogOpen(false) // closes remind dialog
    setSelectedUser(null) // clears selected user
  }

  const handleOpenDeleteDialog = () => {
    setIsDeleteDialogOpen(true) // opens delete dialog
    setAnchorEl(null) // closes popover
  }

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false) // closes delete dialog
    setSelectedUser(null) // clears selected user
  }

  /* --- Start/Stop Dismissal Handler --- */
  const handleStartStopDismissal = async () => {
    const newDismissalActive = !isDismissalActive
    setIsLoading(newDismissalActive ? 'Starting Dismissal...' : 'Stopping Dismissal...')
    const { msg, error } = await updateSchool(schoolName, { dismissalAction: newDismissalActive ? 'start' : 'stop' })
    if (error) {
      setMsg(msg)
      setIsDismissalModalOpen(false)
      setIsLoading('')
    } else {
      onUpdate(() => {
        setIsDismissalActive(newDismissalActive)
        setIsLoading('')
        setIsDismissalModalOpen(false)
      })
    }
  }

  /* --- File Upload Handler --- */
  const handleFileUpload = async (file: File) => {
    const reader = new FileReader()
    reader.onloadend = async (event) => {
      if (event.target) {
        Papa.parse(event.target.result as string, {
          header: true, // Assuming the CSV has a header row
          complete: (result: any) => handleBulkUpload(result.data),
        })
      }
    }
    reader.readAsText(file)
    setIsUploadDialogOpen(false)
  }

  /* --- Upsert Student Handler --- */
  const handleUpsertStudent = async (student: UpsertStudentProps, sendSMS?: boolean) => {
    // Determine if this is an add or edit operation
    const isAdded = !selectedUser

    // Only check for conflicts when adding a new student
    if (isAdded) {
      const existingUser = students?.find((v) => v.userPhoneNumber === student.userPhoneNumber)
      const isProfileMismatch =
        existingUser &&
        (existingUser.userName !== student.userName || existingUser.userRelationship !== student.userRelationship)
      if (isProfileMismatch) {
        setMsg({
          style: 'error',
          text: `This user phone number already exists. If you are adding another student to this user, ensure the user name and relationship matches our system records.`,
        })
        return
      }
    }

    // Upsert student
    setIsLoading(`${isAdded ? 'Adding' : 'Updating'} Student...`)
    const { msg, data, error } = await upsertStudents([student], sendSMS ? (isAdded ? 'SEND' : 'RESEND') : 'SUPPRESS')
    if (error) {
      setMsg(msg)
      setIsLoading('')
      setIsEditDialogOpen(false) // closes edit dialog
      setIsAddDialogOpen(false) // closes add dialog
    } else {
      onUpdate(() => {
        setSelectedUser(null) // clears selected student
        setMsg({
          style: data?.errored?.length ? 'error' : 'success',
          text: `${data?.created?.length ?? 0} added, ${data?.updated?.length ?? 0} updated, ${data?.errored?.length ?? 0} errored.`,
        })
        setIsLoading('')
        setIsEditDialogOpen(false) // closes upsert dialog
        setIsAddDialogOpen(false) // closes add dialog
      })
    }
  }

  /* --- Register User Handler --- */
  const handleRegisterSelectedUsers = async (user?: UpsertStudentProps) => {
    // Build users to register after validation
    let usersToRegister: UpsertStudentProps[] = [],
      errorMessage: string = ''
    if (user && user.userPhoneNumber) {
      // Get single user selected to register
      const isEveryUserUnregistered = body.find((row) => user.userPhoneNumber === row.phone)?.registered === 'No'
      if (!isEveryUserUnregistered) errorMessage = `This user is already registered.`
      else usersToRegister = [user]
    } else if (selectedIds.length > 0) {
      // Get bulk selected users from local state
      const isEveryUserUnregistered = selectedIds.every((id) => body.find((row) => id === row.id)?.registered === 'No')
      if (!isEveryUserUnregistered) errorMessage = `Some users are already registered. Deselect them and try again.`
      else {
        usersToRegister =
          students
            ?.filter((v) => selectedIds.includes(v.studentId))
            .reduce(
              (acc, { userPhoneNumber, userName }) => {
                if (!acc.seen.has(userPhoneNumber)) {
                  acc.seen.add(userPhoneNumber)
                  acc.result.push({
                    userPhoneNumber,
                    userName,
                  })
                }
                return acc
              },
              { seen: new Set<string>(), result: [] as UpsertStudentProps[] },
            ).result ?? []
      }
    }

    if (usersToRegister.length === 0) {
      setMsg({ style: 'error', text: errorMessage || 'No users selected.' })
      setIsRegisterDialogOpen(false) // closes register dialog
      return
    }

    // Register users
    setIsLoading('Sending Signup Link...')
    const { msg, data, error } = await upsertStudents(usersToRegister, 'RESEND')
    if (error) {
      setMsg(msg)
      setIsLoading('')
      setIsRegisterDialogOpen(false) // closes register dialog
    } else {
      // No need to call onUpdate because no changes are made to the table
      if (user)
        setSelectedUser(null) // clears selected user
      else setSelectedIds([]) // clears selected IDs
      setMsg({
        style: data?.errored?.length ? 'error' : 'success',
        text: `${(data?.created?.length ?? 0) + (data?.updated?.length ?? 0)} sent, ${data?.errored?.length ?? 0} failed.`,
      })
      setIsLoading('')
      setIsRegisterDialogOpen(false) // closes register dialog
    }
  }

  /* --- Notify User Handler --- */
  const handleNotifySelectedUsers = async (
    { notificationType, notificationTitle, notificationBody, notificationActionUrl, userIds }: CreateNotificationProps,
    sendSms?: boolean,
    sendPush?: boolean,
  ) => {
    // Build users to notify after validation
    const usersToNotify: CreateNotificationInputProps = {
      notificationType,
      notificationTitle,
      notificationBody,
      notificationActionUrl,
    }
    let errorMessage: string = ''
    if (userIds?.length) {
      // Get single user selected to notify
      const isEveryUserRegistered = userIds.every(
        (userId) => body.find((row) => userId === row.phone)?.registered === 'Yes',
      )
      if (!isEveryUserRegistered) errorMessage = `This user is not registered.`
      usersToNotify.userIds = userIds
    } else if (selectedIds.length > 0) {
      // Get bulk selected users from local state
      const isEveryUserRegistered = selectedIds.every((id) => body.find((row) => id === row.id)?.registered === 'Yes')
      if (!isEveryUserRegistered) errorMessage = `Some users are not registered. Deselect them and try again.`
      else {
        usersToNotify.userIds =
          students
            ?.filter((v) => selectedIds.includes(v.studentId))
            .reduce(
              (acc, { userPhoneNumber }) => {
                if (!acc.seen.has(userPhoneNumber)) {
                  acc.seen.add(userPhoneNumber)
                  acc.result.push(userPhoneNumber)
                }
                return acc
              },
              { seen: new Set<string>(), result: [] as string[] },
            ).result ?? []
      }
    }

    if (notificationType === NotifyTypes.URGENT_REMINDERS && !usersToNotify.userIds?.length) {
      setMsg({ style: 'error', text: errorMessage || 'No users selected.' })
      setIsRemindDialogOpen(false) // closes notify dialog
      setIsAnnouncementModalOpen(false) // closes announcement dialog
      return
    }

    // Notify users
    setIsLoading('Sending Signup Link...')
    const { msg, error } = await createNotification(usersToNotify, sendSms, sendPush)
    if (error) {
      setMsg(msg)
      setIsLoading('')
      setIsRemindDialogOpen(false) // closes notify dialog
      setIsAnnouncementModalOpen(false) // closes announcement dialog
    } else {
      // No need to call onUpdate because no changes are made to the table
      if (userIds?.length)
        setSelectedUser(null) // clears selected user
      else setSelectedIds([]) // clears selected IDs
      if (notificationType === NotifyTypes.URGENT_REMINDERS)
        setMsg({ style: 'success', text: `Sent message to ${usersToNotify.userIds?.length} users.` })
      else if (notificationType === NotifyTypes.COMMUNITY_ANNOUNCEMENTS)
        setMsg({ style: 'success', text: `Announcement sent to all registered users.` })
      else setMsg({ style: 'success', text: `General update sent to opted-in users.` })
      setIsLoading('')
      setIsRemindDialogOpen(false) // closes notify dialog
      setIsAnnouncementModalOpen(false) // closes announcement dialog
    }
  }

  /* --- Delete Student Handler --- */
  const handleDeleteSelectedStudents = async (studentId?: string) => {
    // Build students to delete after validation
    let studentsToDelete: string[] = [],
      errorMessage: string = ''
    const bodyIds = new Set(body.map((row) => row.id))
    if (studentId) {
      // Get single user selected to delete
      const isEveryStudentExists = bodyIds.has(studentId)
      if (!isEveryStudentExists) errorMessage = `This student does not exist.`
      studentsToDelete = [studentId]
    } else if (selectedIds.length > 0) {
      // Get bulk selected users from local state
      const isEveryStudentExists = selectedIds.every((id) => bodyIds.has(id))
      if (!isEveryStudentExists) errorMessage = `Some selected students do not exist. Deselect them and try again.`
      studentsToDelete = selectedIds.map(String)
    }

    if (!studentsToDelete.length) {
      setMsg({ style: 'error', text: errorMessage || 'No students selected.' })
      return
    }

    // Delete students
    setIsLoading('Deleting Student...')
    const { msg, data, error } = await deleteStudents(studentsToDelete)
    if (error) {
      setMsg(msg)
      setIsLoading('')
      setIsDeleteDialogOpen(false) // closes delete dialog
    } else {
      onUpdate(() => {
        if (studentId)
          setSelectedUser(null) // clears selected user
        else setSelectedIds([]) // clears selected IDs
        setMsg({
          style: data?.errored?.length ? 'error' : 'success',
          text: `${data?.deleted?.length ?? 0} deleted, ${data?.errored?.length ?? 0} failed.`,
        })
        setIsLoading('')
        setIsDeleteDialogOpen(false) // closes delete dialog
      })
    }
  }

  const createSelectOptions = (values: string[]) => values.map((value) => ({ value, label: value }))
  const header: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Id',
      headerAlign: 'left',
      type: 'string',
      sortable: false,
      editable: false,
      filterable: false,
      hideable: true,
      visible: false,
      groupable: false,
      disableExport: false,
    },
    {
      field: 'name',
      headerName: 'Name',
      headerAlign: 'left',
      type: 'string',
      sortable: true,
      sortComparator: (v1: string, v2: string) => v1.localeCompare(v2),
      editable: false,
      filterable: true,
      hideable: true,
      visible: true,
      groupable: false,
      disableExport: false,
      flex: 2,
      minWidth: 200,
      resizable: false,
      renderHeader: (params: GridColumnHeaderParams) => <WrappedHeaderText headerName={params.colDef.headerName} />,
      renderCell: (params: GridRenderCellParams<any, string>) => {
        const phone = parsePhoneNumber(params.row.phone).formatNational()
        const isRevealed = revealedPhoneRows.has(params.row.id)
        const masked = phone.length > 4 ? `...${phone.slice(-4)}` : phone
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              height: '100%',
              gap: '10px',
            }}
          >
            <AvatarComponent
              size={8}
              name={params.row.name}
              photoLink={params.row.photoLink}
              failedPhotoLinks={failedAvatarUrls}
              onFailedPhotoLink={onFailedAvatarUrl}
            />
            <div className='flex flex-col items-start justify-center leading-tight gap-1'>
              <TextComponent size='p' className='m-0 leading-tight'>
                {titleCase(params.row.name)}
              </TextComponent>
              {SHOW_MASKED_PHONENUMBER_ON_UI && (
                <TextComponent size='p'>
                  {isRevealed ? phone : masked}
                  <button
                    type='button'
                    onClick={() => handlePhoneRevealToggle(params.row.id)}
                    className='ml-2 align-middle'
                  >
                    <img
                      className='w-4 h-4'
                      src={isRevealed ? show : hide}
                      alt={isRevealed ? 'Hide phone' : 'Show phone'}
                    />
                  </button>
                </TextComponent>
              )}
            </div>
          </div>
        )
      },
    },
    {
      field: 'relationship',
      headerName: 'Relation',
      headerAlign: 'left',
      type: 'string',
      sortable: false,
      editable: false,
      filterable: false,
      hideable: true,
      visible: false,
      groupable: false,
      disableExport: false,
    },
    {
      field: 'student',
      headerName: 'Student',
      headerAlign: 'left',
      type: 'string',
      sortable: true,
      sortComparator: (v1: string, v2: string) => v1.localeCompare(v2),
      editable: false,
      filterable: true,
      hideable: true,
      visible: true,
      groupable: false,
      disableExport: false,
      flex: 1,
      minWidth: defaultColumnWidth,
      resizable: false,
      align: 'center',
      renderHeader: (params: GridColumnHeaderParams) => <WrappedHeaderText headerName={params.colDef.headerName} />,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <TextComponent size='p' className='m-0 leading-tight'>
            {params.row.student}
          </TextComponent>
        </div>
      ),
    },
    schoolScheduleGroups?.length > 1 && {
      field: 'schedule',
      headerName: 'Schedule',
      type: 'singleSelect',
      valueOptions: createSelectOptions(schoolScheduleGroups.map((v) => v.schoolScheduleGroupName)),
      sortable: true,
      editable: false,
      filterable: true,
      hideable: true,
      visible: true,
      groupable: false,
      disableExport: false,
      flex: 1,
      minWidth: defaultColumnWidth,
      resizable: false,
      renderHeader: (params: GridColumnHeaderParams) => <WrappedHeaderText headerName={params.colDef.headerName} />,
    },
    {
      field: 'registered',
      headerName: 'Registered',
      headerAlign: 'center',
      type: 'singleSelect',
      valueOptions: createSelectOptions(['Yes', 'No']),
      sortable: true,
      sortComparator: (v1: string, v2: string) => v1.localeCompare(v2),
      editable: false,
      filterable: true,
      hideable: true,
      visible: true,
      groupable: false,
      disableExport: false,
      width: defaultColumnWidth,
      resizable: false,
      renderHeader: (params: GridColumnHeaderParams) => <WrappedHeaderText headerName={params.colDef.headerName} />,
      renderCell: (params: GridRenderCellParams<any, boolean>) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          {params.row.registered === 'Yes' && <CheckIcon />}
        </div>
      ),
    },
    viewMode === 'ALL' && {
      field: 'lastSeen',
      headerName: 'Last Seen',
      headerAlign: 'center',
      type: 'date',
      valueGetter: (value: string) => (value ? new Date(value) : null),
      valueFormatter: (value: string) =>
        value
          ? new Date(value).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '',
      sortable: true,
      sortComparator: (v1: Date, v2: Date) => {
        console.log(v1, v2)
        const time1 = v1 ? v1.getTime() : 0
        const time2 = v2 ? v2.getTime() : 0
        return time1 - time2
      },
      editable: false,
      filterable: true,
      hideable: true,
      visible: true,
      groupable: false,
      disableExport: false,
      flex: 1,
      minWidth: defaultColumnWidth,
      resizable: false,
      renderHeader: (params: GridColumnHeaderParams) => <WrappedHeaderText headerName={params.colDef.headerName} />,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          {params.row.lastSeen
            ? new Date(params.row.lastSeen).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })
            : ''}
        </div>
      ),
    },
    viewMode === 'DISMISSAL' &&
      dismissalModeDate && {
        field: 'dismissal',
        headerName: 'Arrival Status',
        headerAlign: 'center',
        type: 'string',
        sortable: true,
        sortComparator: <T = any,>(v1: T, v2: T) => {
          const weight = (v: T) => {
            if (v === 'Arrived') return 2
            if (v === 'On my way') return 1
            return 0
          }
          return weight(v1) - weight(v2)
        },
        editable: false,
        filterable: true,
        hideable: true,
        visible: true,
        groupable: false,
        disableExport: false,
        width: defaultColumnWidth,
        resizable: false,
        align: 'center',
        renderHeader: (params: GridColumnHeaderParams) => <WrappedHeaderText headerName={params.colDef.headerName} />,
        renderCell: (params: GridRenderCellParams<any, string>) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            {params.row.dismissal.status === 'Arrived' && <ArrivedLabel />}
            {params.row.dismissal.status === 'On my way' && <OnMyWayLabel />}
          </div>
        ),
      },
    viewMode === 'CARPOOL' && {
      field: 'morningCarpoolRequests',
      headerName: 'Morning',
      headerAlign: 'center',
      type: 'singleSelect',
      valueOptions: createSelectOptions(['Morning - Paid Transport', 'Morning - Share Carpool', 'Morning - Inactive']),
      sortable: true,
      sortComparator: (v1: string, v2: string) => v1.localeCompare(v2),
      editable: false,
      filterable: true,
      hideable: true,
      visible: true,
      groupable: false,
      disableExport: false,
      width: defaultColumnWidth,
      resizable: false,
      align: 'center',
      renderHeader: (params: GridColumnHeaderParams) => <WrappedHeaderText headerName={params.colDef.headerName} />,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          {params.row.morningCarpoolRequests === 'Morning - Paid Transport' && <DollarIcon />}
          {params.row.morningCarpoolRequests === 'Morning - Share Carpool' && <CheckIcon />}
        </div>
      ),
    },
    viewMode === 'CARPOOL' && {
      field: 'afternoonCarpoolRequests',
      headerName: 'Afternoon',
      headerAlign: 'center',
      type: 'singleSelect',
      valueOptions: createSelectOptions([
        'Afternoon - Paid Transport',
        'Afternoon - Share Carpool',
        'Afternoon - Inactive',
      ]),
      sortable: true,
      sortComparator: (v1: string, v2: string) => v1.localeCompare(v2),
      editable: false,
      filterable: true,
      hideable: true,
      visible: true,
      groupable: false,
      disableExport: false,
      width: defaultColumnWidth,
      resizable: false,
      align: 'center',
      renderHeader: (params: GridColumnHeaderParams) => <WrappedHeaderText headerName={params.colDef.headerName} />,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          {params.row.afternoonCarpoolRequests === 'Afternoon - Paid Transport' && <DollarIcon />}
          {params.row.afternoonCarpoolRequests === 'Afternoon - Share Carpool' && <CheckIcon />}
        </div>
      ),
    },
    viewMode === 'CARPOOL' && {
      field: 'messagesSent',
      headerName: 'Msgs Sent',
      headerAlign: 'center',
      type: 'number',
      sortable: true,
      sortComparator: (v1: number, v2: number) => v1 - v2,
      editable: false,
      filterable: true,
      hideable: true,
      visible: true,
      groupable: false,
      disableExport: false,
      width: defaultColumnWidth,
      resizable: false,
      align: 'center',
      renderHeader: (params: GridColumnHeaderParams) => <WrappedHeaderText headerName={params.colDef.headerName} />,
      renderCell: (params: GridRenderCellParams<any, number>) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          {params.row.messagesSent}
        </div>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      headerAlign: 'left',
      type: 'string',
      valueGetter: (value: string) => parsePhoneNumber(value).formatNational(),
      sortable: false,
      editable: false,
      filterable: true,
      hideable: true,
      visible: false,
      groupable: false,
      disableExport: false,
      width: defaultColumnWidth,
      resizable: false,
      align: 'center',
      renderHeader: (params: GridColumnHeaderParams) => <WrappedHeaderText headerName={params.colDef.headerName} />,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <TextComponent size='p' className='m-0 leading-tight'>
            {params.value}
          </TextComponent>
        </div>
      ),
    },
    viewMode === 'ALL' && {
      field: 'action',
      headerName: '',
      headerAlign: 'left',
      type: 'string',
      sortable: false,
      editable: false,
      filterable: true,
      hideable: true,
      visible: true,
      groupable: false,
      disableExport: true,
      width: 75,
      resizable: false,
      align: 'center',
      renderHeader: (params: GridColumnHeaderParams) => <WrappedHeaderText headerName={params.colDef.headerName} />,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <IconButton onClick={(e) => handleOpenMenu(e, params.row)}>
            <MoreVerticalDotOnlyIcon />
          </IconButton>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  border: `1px solid #D0D5DD`,
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  boxShadow: 'none',
                  minWidth: 140,
                  p: 0.5,
                },
              },
            }}
          >
            <MenuList
              disablePadding
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                p: 0,
                width: 140,
                boxShadow: 'none',
                [`& .${menuItemClasses.root}`]: {
                  border: 'none',
                  borderRadius: '8px',
                  my: 0.5,
                  '&:hover': {
                    backgroundColor: '#F5F5F5',
                  },
                },
              }}
            >
              {selectedUser?.registered === 'No' && (
                <MenuItem onClick={handleOpenRegisterDialog} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SendIcon />
                  Register
                </MenuItem>
              )}

              <MenuItem onClick={handleOpenEditDialog} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditUserIcon />
                Edit
              </MenuItem>

              {isCommunityAnnouncementsActive && selectedUser?.registered === 'Yes' && (
                <MenuItem onClick={handleOpenRemindDialog} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmsIcon />
                  Message
                </MenuItem>
              )}

              <MenuItem onClick={handleOpenDeleteDialog} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeleteIcon />
                Delete
              </MenuItem>
            </MenuList>
          </Popover>
        </div>
      ),
    },
  ].filter(Boolean) as GridColDef[] // Ensure non-null columns

  // --- Data for the Table ---
  const getCarpoolRequestStatus = (isPickup: boolean, isHire: boolean, type: 'Morning' | 'Afternoon') => {
    const prefix = `${type} - `
    if (isPickup && isHire) return `${prefix}Paid Transport`
    if (isPickup) return `${prefix}Share Carpool`
    return `${prefix}Inactive`
  }
  const body: GridRowsProp =
    students
      ?.filter((v: SchoolUsersStudentProps) => v.userRelationship !== 'SchoolAdmin')
      .map((v: SchoolUsersStudentProps) => {
        const {
          studentId,
          userPhoneNumber,
          userName = '',
          userRelationship,
          userPhotoLink,
          userSentMessages,
          userLastSeen = '',
          studentName = '',
          scheduleGroupName,
          pickupVehicle,
          dropoffVehicle,
          isVerified,
          isPickup,
          isDropoff,
        } = v
        const isHirePickupSvc = isPaidTransportFeatureEnabled && pickupVehicle === VehicleProps.HIRE_TRANSPORT_SERVICE
        const isHireDropoffSvc = isPaidTransportFeatureEnabled && dropoffVehicle === VehicleProps.HIRE_TRANSPORT_SERVICE
        return {
          communityType: CommunityType.SCHOOL,
          id: studentId,
          phone: userPhoneNumber,
          name: userName,
          relationship: userRelationship,
          photoLink: userPhotoLink,
          student: studentName,
          schedule: scheduleGroupName,
          registered: isVerified ? 'Yes' : 'No',
          lastSeen: userLastSeen,
          morningCarpoolRequests: getCarpoolRequestStatus(isPickup, isHirePickupSvc, 'Morning'),
          afternoonCarpoolRequests: getCarpoolRequestStatus(isDropoff, isHireDropoffSvc, 'Afternoon'),
          messagesSent: userSentMessages,
          dismissal: {
            status: '',
            pin: '',
          },
        }
      }) ?? []

  return (
    <>
      {/* --- Toast Messages --- */}
      {msg && (
        <ToastComponent style={msg?.style} heading={msg?.heading} text={msg?.text} onClose={() => setMsg(undefined)} />
      )}

      {/* --- Loading Dialog --- */}
      {isLoading && (
        <Dialog open={true} sx={{ zIndex: 1400 }}>
          <DialogTitle>{isLoading}</DialogTitle>
          <CircularProgressWithLabel />
        </Dialog>
      )}

      {/* --- Main page header and table --- */}
      <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-5'>
        {/* --- Header Left Elements --- */}
        <div className='flex items-center gap-4'>
          {/* --- Radio Buttons for View Mode --- */}
          <RadioMobileComponent
            row={true}
            radios={[
              {
                isChecked: viewMode === 'ALL',
                label: <span data-testid='radio-all-testid'>All</span>,
                onChange: () => setViewMode('ALL'),
                disable: isDismissalActive,
                data_testid: 'radio-all-testid',
              },
              ...(isCarpoolManagementActive
                ? [
                    {
                      isChecked: viewMode === 'CARPOOL',
                      label: <span data-testid='radio-carpool-testid'>Carpool</span>,
                      onChange: () => setViewMode('CARPOOL'),
                      disable: isDismissalActive,
                      data_testid: 'radio-carpool-testid',
                    },
                  ]
                : []),
              ...(isDismissalManagementActive
                ? [
                    {
                      isChecked: viewMode === 'DISMISSAL',
                      label: (
                        <span
                          className={isDismissalActive ? 'text-red-600 font-medium' : ''}
                          data-testid='radio-dismissal-testid'
                        >
                          Dismissal
                        </span>
                      ),
                      onChange: () => setViewMode('DISMISSAL'),
                      disable: isDismissalActive,
                      data_testid: 'radio-dismissal-testid',
                    },
                  ]
                : []),
            ]}
          />
          {/* --- Dismissal Mode Date Picker --- */}
          {isDismissalManagementActive && viewMode === 'DISMISSAL' && (
            <div className='flex items-center'>
              <input
                type='date'
                value={dismissalModeDate}
                onChange={(e) => setDismissalModeDate(e.target.value)}
                disabled={isDismissalActive}
                className='border rounded px-2 py-1 text-sm'
              />
            </div>
          )}
        </div>

        {/* --- Header Right Elements --- */}
        <div className='flex items-center gap-3'>
          {/* Start/Stop Dismissal */}
          {isDismissalManagementActive && (
            <div>
              {/* --- Dismissal Button --- */}
              <ButtonMobileComponent
                text={
                  isDismissalActive
                    ? stopDismissalButtonConfig[deviceSize].text
                    : startDismissalButtonConfig[deviceSize].text
                }
                data-testid='dismissal-button'
                color={isDismissalActive ? 'bg-custom-red' : 'bg-green-600'}
                textColor={isDismissalActive ? 'text-white' : 'text-white'}
                width={
                  isDismissalActive
                    ? stopDismissalButtonConfig[deviceSize].width
                    : startDismissalButtonConfig[deviceSize].width
                }
                padx={
                  isDismissalActive
                    ? stopDismissalButtonConfig[deviceSize].padx
                    : startDismissalButtonConfig[deviceSize].padx
                }
                onClick={() => setIsDismissalModalOpen(true)}
                className='transition-colors duration-200 group border border-[#D0D5DD] text-[14px]'
              />
              {/* --- Dismissal Confirmation Modal --- */}
              <ModalConfirmComponent
                isShow={isDismissalModalOpen}
                title={!isDismissalActive ? 'Start Dismissal Process?' : 'Stop Dismissal Process?'}
                body={
                  !isDismissalActive
                    ? 'Allow families to begin the check-in process through the app'
                    : 'Disables family check-ins and admin pickup status updates'
                }
                actionIcon={!isDismissalActive ? 'yellowPowerIcon' : 'redInfoIcon'}
                actionText={!isDismissalActive ? 'Start' : 'Stop'}
                onActionCancel={() => {
                  setIsDismissalModalOpen(false)
                }}
                onActionSubmit={handleStartStopDismissal}
              />
            </div>
          )}
          {/* Message Everyone */}
          {isCommunityAnnouncementsActive && (
            <div>
              {/* --- Message Button --- */}
              <ButtonMobileComponent
                iconPosition={'left'}
                text={messageButtonConfig[deviceSize].text}
                data-testid='announcement-button'
                icon={
                  <div className='hidden sm:block'>
                    <MegaphoneIcon />
                  </div>
                }
                color='bg-custom-yellow'
                width={messageButtonConfig[deviceSize].width}
                padx={messageButtonConfig[deviceSize].padx}
                onClick={() => setIsAnnouncementModalOpen(true)}
                className='transition-colors duration-200 group border border-[#D0D5DD] text-[14px]'
              />
              {/* --- Message Modal Is Shared with CustomToolbar inside the Table Component --- */}
            </div>
          )}
          {/* Upload Students */}
          <div>
            {/* --- Upload Button --- */}
            <ButtonMobileComponent
              iconPosition={'left'}
              text={uploadButtonConfig[deviceSize].text}
              data-testid='upload-button'
              icon={
                <div className='hidden sm:block'>
                  <UploadIcon />
                </div>
              }
              color='bg-custom-yellow'
              width={uploadButtonConfig[deviceSize].width}
              padx={uploadButtonConfig[deviceSize].padx}
              onClick={() => setIsUploadDialogOpen(true)}
              className='transition-colors duration-200 group border border-[#D0D5DD] text-[14px]'
            />
            {/* --- Upload File Modal --- */}
            <FileUpload
              isShow={isUploadDialogOpen}
              onActionCancel={() => {
                setIsUploadDialogOpen(false)
              }}
              onActionSubmit={handleFileUpload}
            />
          </div>
          {/* Add Student */}
          <div>
            {/* --- Add Button --- */}
            <ButtonMobileComponent
              iconPosition={'left'}
              text={addButtonConfig[deviceSize].text}
              data-testid='add-user-button'
              icon={
                <div className='hidden sm:block'>
                  <PlusIcon />
                </div>
              }
              color='bg-custom-yellow'
              width={addButtonConfig[deviceSize].width}
              padx={addButtonConfig[deviceSize].padx}
              onClick={() => setIsAddDialogOpen(true)}
              className='transition-colors duration-200 group border border-[#D0D5DD] text-[14px]'
            />
            {/* --- Add Modal Is Shared with CustomToolbar inside the Table Component --- */}
          </div>
        </div>
      </div>

      {/* --- User Table --- */}
      {body && (
        <TableComponent
          header={header}
          body={body.slice()}
          selectedIds={selectedIds}
          onSelectionChange={handleRowSelectionChange}
          onRegisterClick={() => setIsRegisterDialogOpen(true)}
          onNotifyClick={isCommunityAnnouncementsActive ? () => setIsRemindDialogOpen(true) : undefined}
          onDeleteClick={() => setIsDeleteDialogOpen(true)}
          csvOptions={{ allColumns: true, fileName: `${titleCaseSchoolNameForUI(schoolName)} Family Users` }}
          printOptions={{ disableToolbarButton: true }}
        >
          {/* All components used by toolbar need to go here in the TableComponent */}
          {/* --- Upsert Student Modal --- */}
          <UpsertStudentComponent
            isShow={isAddDialogOpen || isEditDialogOpen}
            isRegistered={selectedUser?.registered === 'Yes' ? true : false}
            id={selectedUser?.id || ''}
            name={selectedUser?.name || ''}
            phone={selectedUser?.phone || ''}
            relationship={selectedUser?.relationship || ''}
            student={selectedUser?.student || ''}
            schedule={selectedUser?.schedule || ''}
            scheduleGroupNames={schoolScheduleGroups?.map((v: ScheduleGroupProps) => v.schoolScheduleGroupName)}
            onActionCancel={() => {
              if (isAddDialogOpen) setIsAddDialogOpen(false)
              else handleCloseEditDialog()
            }}
            onActionSubmit={handleUpsertStudent}
          />
          {/* --- Register User Modal --- */}
          <RegisterUserComponent
            isShow={isRegisterDialogOpen}
            name={selectedUser?.name || ''}
            phone={selectedUser?.phone || ''}
            onActionCancel={handleCloseRegisterDialog}
            onActionSubmit={handleRegisterSelectedUsers}
          />
          {/* --- Notify User Modal --- */}
          <NotifyUserComponent
            isShow={isAnnouncementModalOpen || isRemindDialogOpen}
            title={
              isAnnouncementModalOpen
                ? 'Send Announcement to All Families'
                : selectedUser?.phone
                  ? 'Message family'
                  : 'Send to selected families'
            }
            allowableNotifyTypes={
              isAnnouncementModalOpen
                ? [NotifyTypes.COMMUNITY_ANNOUNCEMENTS, NotifyTypes.GENERAL_UPDATES]
                : [NotifyTypes.URGENT_REMINDERS]
            }
            smsFeature={isCommunityAnnouncementsActive}
            phone={selectedUser?.phone || ''}
            onActionCancel={() => {
              if (isAnnouncementModalOpen) setIsAnnouncementModalOpen(false)
              else handleCloseRemindDialog()
            }}
            onActionSubmit={handleNotifySelectedUsers}
          />
          {/* --- Delete Student Modal --- */}
          <DeleteStudentComponent
            isShow={isDeleteDialogOpen}
            studentId={selectedUser?.id || ''}
            onActionCancel={handleCloseDeleteDialog}
            onActionSubmit={handleDeleteSelectedStudents}
          />
        </TableComponent>
      )}
    </>
  )
}

export default Index
